/**
 * CallToActionSection.tsx - Call-to-Action Configuration Section
 *
 * This component provides configuration options for the call-to-action (CTA)
 * section including text, icons, images, and display preferences.
 *
 * Key Features:
 * - CTA title and description customization
 * - Button text configuration
 * - Icon selection and color theming
 * - Custom image upload for CTA background
 * - Display mode selection (icon, image, or none)
 * - Real-time preview of CTA appearance
 *
 * Configuration Options:
 * - Title: Main CTA headline text
 * - Description: Supporting text for the CTA
 * - Button Text: Customizable donation button text
 * - Icon Choice: Icon, image, or no visual element
 * - Icon Selection: Choose from Lucide icon library
 * - Icon Color: Custom color theming for icons
 * - Image Upload: Custom background image for CTA
 *
 * Display Modes:
 * - Icon Mode: Icon with text and button
 * - Image Mode: Custom image with overlay button
 * - None: Text-only CTA without visual elements
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: CallToAction preview component, icon selector, image upload
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Plus, X, Edit2 } from "lucide-react";
import { CallToAction } from "../../CallToAction";
import { EditMediaModal } from "../../media-editor/EditMediaModal";
import {
  CreateFundraiserModel,
  FundraiserCustomColorConfig,
  MediaModel,
} from "../../../models";
import { IconType } from "../../../enums";
import { colorTemplates } from "../design";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewContainer = styled.div`
  position: sticky;
  top: 32px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  border: none;
  min-height: 280px;
  max-width: 380px;
  width: 100%;
  margin: 0 auto;
  align-self: center; /* Horizontally center the preview container */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PreviewTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  text-align: center;
`;

const PreviewWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconImageSection = styled.div`
  margin-bottom: 32px;
`;

const ChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IconImageRow = styled.div`
  display: grid;
  grid-template-columns: 300px 130px;
  gap: 22px;
  align-items: end;
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IconSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  &.icon-section {
    display: unset;
  }
`;

const ColorSection = styled.div``;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const ChoiceSection = styled.div`
  display: flex;
  gap: 0;
`;

const ChoiceButtons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ChoiceButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  height: 44px;
  border: 2px solid ${(props) => (props.active ? "#3b82f6" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.active ? "#f0f9ff" : "#ffffff")};
  color: ${(props) => (props.active ? "#3b82f6" : "#64748b")};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const IconSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-width: 150px;
  width: auto;
  max-width: 200px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  &.icon-preview-btn {
    min-width: 150px;
    width: auto;
    max-width: 200px;
  }
`;

const ColorPicker = styled.input`
  width: 44px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const EditIconButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: none;
  z-index: 10;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    transform: translateY(-50%) scale(1.05);
    box-shadow: none;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const ShowButtonCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 36px;
  margin-bottom: 25px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
`;

const ButtonInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 16px;
`;

const ButtonInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const ReducedWidthInput = styled.input`
  width: 80%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const LabelWithCounter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 200px;
`;

const CharacterCounter = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const ReducedWidthTextArea = styled.textarea`
  width: 80%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const OldIconSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #3b82f6;
  }
`;

const ColoredIconPreview = styled.div<{ bgColor: string; iconColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.iconColor};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const LargeImagePreview = styled.img`
  max-width: 400px;
  max-height: 240px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  cursor: pointer;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 200px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
  }
`;

const UploadIcon = styled.div`
  color: #9ca3af;
  margin-bottom: 8px;
`;

const UploadText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  transition: color 0.2s ease;
`;

const UploadSubtext = styled.span`
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.2s ease;
`;

const FileInput = styled.input`
  display: none;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const RedHelpText = styled.span`
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
`;

interface CallToActionSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  renderIcon: (iconName: string) => React.ReactNode;
  getIconColors: (color: string) => { bgColor: string; iconColor: string };
  setShowIconSelector: (show: boolean) => void;
  setIconSelectorImpactId: (id: string) => void;
  handleCtaImageUpload: (files: FileList | null) => void;
  onKeyDown?: (e: React.KeyboardEvent, isTextArea?: boolean) => void;
}

export const CallToActionSection: React.FC<CallToActionSectionProps> = observer(
  ({
    builderData,
    updateBuilderField,
    renderIcon,
    getIconColors,
    setShowIconSelector,
    setIconSelectorImpactId,
    handleCtaImageUpload,
    onKeyDown,
  }) => {
    const [showEditMediaModal, setShowEditMediaModal] = useState(false);
    const [editingMediaItem, setEditingMediaItem] = useState<MediaModel | null>(
      null
    );
    const [showCtaButton, setShowCtaButton] = useState(
      builderData.showCtaButton || false
    );

    const hasCtaImage =
      builderData.ctaImage && builderData.ctaIconImageChoice === IconType.Image;
    const ctaIconImageChoice = builderData.ctaIconImageChoice || IconType.Icon;

    // Use Design colors for CTA icon
    const ctaIconColor =
      builderData.customColors?.background.hex ||
      builderData.customColors?.background.rgba ||
      "#FFFFFF";
    const ctaIconBgColor =
      builderData.customColors?.secondary.hex ||
      builderData.customColors?.secondary.rgba ||
      "#3986EF";

    const handleCtaChoiceChange = (choice: string) => {
      updateBuilderField("ctaIconImageChoice", choice);

      // Set default values when switching to None or Icon
      if (choice === "none" || choice === "icon") {
        if (
          !builderData.ctaButtonText ||
          builderData.ctaButtonText.trim() === ""
        ) {
          updateBuilderField("ctaButtonText", "Donate");
        }
        if (!builderData.ctaTitle || builderData.ctaTitle.trim() === "") {
          updateBuilderField("ctaTitle", "Make a Difference Today");
        }
        if (
          !builderData.ctaDescription ||
          builderData.ctaDescription.trim() === ""
        ) {
          updateBuilderField(
            "ctaDescription",
            "Your donation is critically important to achieving our fundraising goals."
          );
        }
        // Icon colors now come from Design step, no need to set ctaIconColor
      }

      // Reset show button checkbox when switching away from image
      if (choice !== "image") {
        setShowCtaButton(false);
        updateBuilderField("showCtaButton", false);
      } else {
        // When switching to image mode, set default to false (off)
        setShowCtaButton(false);
        updateBuilderField("showCtaButton", false);
      }
    };

    const handleShowCtaButtonChange = (checked: boolean) => {
      setShowCtaButton(checked);
      updateBuilderField("showCtaButton", checked);
    };

    const handleEditCtaImageClick = () => {
      if (builderData.ctaImage) {
        const mediaItem = new MediaModel({
          ...builderData.ctaImage,
        });
        setEditingMediaItem(mediaItem);
        setShowEditMediaModal(true);
      }
    };

    const handleEditMediaSave = (updatedMedia: MediaModel) => {
      updateBuilderField("ctaImage", new MediaModel({ ...updatedMedia }));
      setShowEditMediaModal(false);
      setEditingMediaItem(null);
    };

    const shouldHidePreview =
      ctaIconImageChoice === IconType.Image &&
      (!showCtaButton || !builderData.ctaImage);

    return (
      <>
        <Section
          style={{ gridTemplateColumns: shouldHidePreview ? "1fr" : "1fr 1fr" }}
        >
          <LeftColumn>
            <IconImageSection>
              <ChoiceContainer>
                <IconImageRow>
                  <ButtonSection>
                    <Label>Icon / Image</Label>
                  </ButtonSection>
                  {ctaIconImageChoice === IconType.Icon && (
                    <IconSection>
                      <Label>Icon</Label>
                    </IconSection>
                  )}
                </IconImageRow>
                <IconImageRow>
                  <ButtonSection>
                    <ChoiceSection>
                      <ChoiceButtons>
                        <ChoiceButton
                          type="button"
                          active={ctaIconImageChoice === IconType.None}
                          onClick={() => handleCtaChoiceChange(IconType.None)}
                        >
                          None
                        </ChoiceButton>
                        <ChoiceButton
                          type="button"
                          active={ctaIconImageChoice === IconType.Icon}
                          onClick={() => handleCtaChoiceChange(IconType.Icon)}
                        >
                          Icon
                        </ChoiceButton>
                        <ChoiceButton
                          type="button"
                          active={ctaIconImageChoice === IconType.Image}
                          onClick={() => handleCtaChoiceChange(IconType.Image)}
                        >
                          Image
                        </ChoiceButton>
                      </ChoiceButtons>
                    </ChoiceSection>
                  </ButtonSection>

                  {ctaIconImageChoice === IconType.Icon && (
                    <IconSection className="icon-section">
                      <IconSelectorButton
                        type="button"
                        onClick={() => {
                          setIconSelectorImpactId("cta");
                          setShowIconSelector(true);
                        }}
                        className="icon-preview-btn"
                      >
                        <ColoredIconPreview
                          bgColor={ctaIconBgColor}
                          iconColor={ctaIconColor}
                        >
                          {renderIcon(builderData.ctaIcon || "Heart")}
                        </ColoredIconPreview>
                        {builderData.ctaIcon || "Heart"}
                      </IconSelectorButton>
                    </IconSection>
                  )}
                </IconImageRow>

                {ctaIconImageChoice === IconType.Image && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        {builderData.ctaImage.fullURL ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <ImageContainer>
                              <LargeImagePreview
                                src={builderData.ctaImage.fullURL || ""}
                                alt="Call to Action Image"
                                onClick={() =>
                                  document
                                    .getElementById("cta-image-input")
                                    ?.click()
                                }
                                style={{ cursor: "pointer" }}
                              />
                              <ImageDeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBuilderField("ctaImage", null);
                                  // Reset showCtaButton when image is removed
                                  setShowCtaButton(false);
                                  updateBuilderField("showCtaButton", false);
                                }}
                                type="button"
                                title="Remove image"
                              >
                                <X size={12} />
                              </ImageDeleteButton>
                            </ImageContainer>
                            <EditIconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCtaImageClick();
                              }}
                              type="button"
                              title="Edit image"
                              style={{
                                position: "static",
                                width: "32px",
                                height: "32px",
                                marginLeft: "15px",
                              }}
                            >
                              <Edit2 size={16} />
                            </EditIconButton>
                          </div>
                        ) : (
                          <ImageUploadArea
                            onClick={() =>
                              updateBuilderField(
                                "showCallToActionSectionLibrary",
                                true
                              )
                            }
                          >
                            <UploadIcon>
                              <Plus size={32} />
                            </UploadIcon>
                            <UploadText>Add Image</UploadText>
                          </ImageUploadArea>
                        )}

                        <FileInput
                          id="cta-image-input"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            handleCtaImageUpload(e.target.files);
                            // When image is uploaded, ensure checkbox starts as false (off)
                            if (e.target.files && e.target.files.length > 0) {
                              setShowCtaButton(false);
                              updateBuilderField("showCtaButton", false);
                            }
                          }}
                        />

                        {/* Only show checkbox AFTER image is added */}
                        {builderData.ctaImage && (
                          <ShowButtonCheckbox>
                            <Checkbox
                              type="checkbox"
                              checked={showCtaButton}
                              onChange={(e) =>
                                handleShowCtaButtonChange(e.target.checked)
                              }
                            />
                            <CheckboxLabel>Show button on image</CheckboxLabel>
                          </ShowButtonCheckbox>
                        )}

                        {/* Show button input when checkbox is checked */}
                        {showCtaButton && (
                          <ButtonInputRow>
                            <ButtonInputSection>
                              <LabelWithCounter>
                                <Label
                                  htmlFor="ctaButtonText"
                                  style={{ marginBottom: 0 }}
                                >
                                  Button
                                </Label>
                                <CharacterCounter>
                                  {(builderData.ctaButtonText || "").length} of
                                  15 characters
                                </CharacterCounter>
                              </LabelWithCounter>
                              <ReducedWidthInput
                                id="ctaButtonText"
                                type="text"
                                placeholder="Donate"
                                value={builderData.ctaButtonText || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 15) {
                                    updateBuilderField("ctaButtonText", value);
                                  }
                                }}
                                maxLength={15}
                                style={{ width: "200px" }}
                                onKeyDown={(e) => {
                                  if (onKeyDown) {
                                    onKeyDown(e, true);
                                  }
                                }}
                              />
                            </ButtonInputSection>
                          </ButtonInputRow>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </ChoiceContainer>
            </IconImageSection>

            {(ctaIconImageChoice === IconType.None ||
              ctaIconImageChoice === IconType.Icon) && (
              <FormGrid>
                <FormGroup>
                  <LabelWithCounter>
                    <Label htmlFor="ctaButtonText" style={{ marginBottom: 0 }}>
                      Button
                    </Label>
                    <CharacterCounter>
                      {(builderData.ctaButtonText || "").length} of 15
                      characters
                    </CharacterCounter>
                  </LabelWithCounter>
                  <ReducedWidthInput
                    id="ctaButtonText"
                    style={{ width: "200px" }}
                    type="text"
                    placeholder="Donate"
                    value={builderData.ctaButtonText || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 15) {
                        updateBuilderField("ctaButtonText", value);
                      }
                    }}
                    maxLength={15}
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithCounter>
                    <Label htmlFor="ctaTitle" style={{ marginBottom: 0 }}>
                      Title
                    </Label>
                    <CharacterCounter>
                      {(builderData.ctaTitle || "").length} of 30 characters
                    </CharacterCounter>
                  </LabelWithCounter>
                  <ReducedWidthInput
                    id="ctaTitle"
                    type="text"
                    placeholder="Make a Difference Today"
                    value={builderData.ctaTitle || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 30) {
                        updateBuilderField("ctaTitle", value);
                      }
                    }}
                    maxLength={30}
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithCounter>
                    <Label htmlFor="ctaDescription" style={{ marginBottom: 0 }}>
                      Description
                    </Label>
                    <CharacterCounter>
                      {(builderData.ctaDescription || "").length} of 100
                      characters
                    </CharacterCounter>
                  </LabelWithCounter>
                  <ReducedWidthTextArea
                    id="ctaDescription"
                    placeholder="Your donation is critically important to achieving our fundraising goals."
                    value={builderData.ctaDescription || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        updateBuilderField("ctaDescription", value);
                      }
                    }}
                    maxLength={100}
                    rows={2}
                    style={{ minHeight: "100px", resize: "none" }}
                    onKeyDown={(e) => {
                      if (onKeyDown) {
                        onKeyDown(e, true);
                      }
                    }}
                  />
                </FormGroup>
              </FormGrid>
            )}
          </LeftColumn>

          {(ctaIconImageChoice === IconType.None ||
            ctaIconImageChoice === IconType.Icon ||
            (ctaIconImageChoice === IconType.Image &&
              builderData.ctaImage &&
              showCtaButton)) &&
            !shouldHidePreview && (
              <RightColumn
                style={{
                  display:
                    ctaIconImageChoice === IconType.Image && !showCtaButton
                      ? "none"
                      : "flex",
                }}
              >
                <PreviewContainer>
                  <PreviewTitle>Preview</PreviewTitle>
                  <PreviewWrapper>
                    <CallToAction
                      customColors={builderData.getCustomColors()}
                      ctaIconColor={ctaIconColor}
                      ctaIconBgColor={ctaIconBgColor}
                      isPreview={true}
                      onDonateClick={() => {}}
                      buttonText={builderData.ctaButtonText || "Donate"}
                      showIcon={ctaIconImageChoice === IconType.Icon}
                      showButtonOnImage={showCtaButton}
                      isFullWidth={false}
                    />
                  </PreviewWrapper>
                </PreviewContainer>
              </RightColumn>
            )}
        </Section>

        {showEditMediaModal && editingMediaItem?.fullURL && (
          <EditMediaModal
            isVisible={showEditMediaModal}
            mediaItem={editingMediaItem}
            onClose={() => {
              setShowEditMediaModal(false);
              setEditingMediaItem(null);
            }}
            onSave={handleEditMediaSave}
            showDetailsPanel={false}
          />
        )}
      </>
    );
  }
);
